import React from 'react';
import { Link } from 'react-router-dom';
import { TRIP_TEMPLATES } from '../data/tripTemplates';
import { useSEO } from '../hooks/useSEO';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const categories = ['romantic', 'adventure', 'wellness', 'cultural', 'family', 'foodie'];

const DestinationsIndexPage: React.FC = () => {
  useSEO({
    title: 'Luxury Travel Destinations — Aura Traveler',
    description: 'Curated luxury travel itineraries for the world\'s most extraordinary destinations. Explore romantic, adventure, wellness, cultural, family, and foodie escapes.',
    canonical: 'https://www.auratraveler.com/destinations',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'Destinations', url: 'https://www.auratraveler.com/destinations' },
    ],
  });

  return (
    <div className="min-h-screen bg-charcoal text-white">
      <SiteNav />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Luxury Travel Destinations — Aura Traveler",
        "description": "Curated luxury travel itineraries for the world's most extraordinary destinations"
      }) }} />

      <main className="max-w-6xl mx-auto px-6 py-20">
        <h1 className="font-display text-4xl md:text-5xl text-white mb-3 text-center">Destinations</h1>
        <p className="font-serif text-gray-400 text-center italic mb-16 max-w-xl mx-auto">Curated luxury itineraries for the world's most extraordinary places</p>

        {categories.map(cat => {
          const templates = TRIP_TEMPLATES.filter(t => t.category === cat);
          if (templates.length === 0) return null;
          return (
            <section key={cat} className="mb-16">
              <h2 className="font-display text-lg uppercase tracking-[0.3em] text-gold mb-6 capitalize">{cat} Escapes</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(t => (
                  <Link key={t.id} to={`/destinations/${t.id}`} className="group relative overflow-hidden rounded-sm aspect-[4/3]">
                    <img src={t.heroImage} alt={`Luxury travel destination: ${t.destination}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-display text-white text-lg">{t.destination}</h3>
                      <p className="text-gray-300 text-xs font-serif">{t.duration} · {t.priceRange || 'From $2,500'}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </main>
      <SiteFooter />
    </div>
  );
};

export default DestinationsIndexPage;
